import {
  BUILDER_API,
  EXECUTOR_API,
  BrainFile,
  BrainFileContent,
  BrainSaveResponse,
  BrainDeleteResponse,
  BrainDeleteFolderResponse,
  BrainIndexingResult,
  BrainPathIndexState,
  AgentConfig,
  AgentSummary,
  AgentListResponse,
  AgentNodesResponse,
  BackendNode,
  BuilderConfigResponse,
  NodeConfigResponse,
  ActivationResponse,
  ErrorResponse,
  FlowDefinitionPayload,
  FlowSaveResponse,
  FlowGetResponse,
  MeetingDoubtsRequest,
  MeetingDoubtsResponse,
} from '../types';
import { buildAuthHeaders } from '../utils/userStore';
import type {
  BrainIndexStatus,
  BrainReindexResponse,
  McpAccessRule,
  McpActivityResponse,
  McpCatalog,
  McpDefaultAccess,
  McpTenantAccess,
  McpTenantServerAccess,
  McpCollectionPolicyInput,
  McpCollectionsResponse,
  McpPolicyResponse,
  McpSampleFieldsResponse,
  McpServerStatus,
} from '../types/mcp';

/**
 * Cabeceras de identidad/tenant para CUALQUIER petición (lectura o escritura).
 * Sin ellas el backend trata la llamada como dev→superadmin y devolvería
 * recursos de todos los tenants, así que también van en las GET.
 * La construcción vive en userStore (fuente única para todo el frontend).
 */
const authHeaders = buildAuthHeaders;

/** Igual que authHeaders pero con Content-Type JSON, para peticiones con body. */
function writeHeaders(extra: Record<string, string> = {}): Record<string, string> {
  return buildAuthHeaders({ 'Content-Type': 'application/json', ...extra });
}

// ── Fetch All Agents ──
export async function fetchAgents(): Promise<AgentSummary[]> {
  const res = await fetch(`${BUILDER_API}/agent/builder/agents`, { headers: authHeaders() });

  if (!res.ok) {
    throw new Error('Error fetching agents');
  }

  const data = (await res.json()) as AgentListResponse;
  return Array.isArray(data.agents) ? data.agents : [];
}

// ── Fetch Agent Nodes ──
export async function fetchAgentNodes(agentId: string): Promise<BackendNode[]> {
  const res = await fetch(`${BUILDER_API}/agent/builder/agents/${agentId}/nodes`, { headers: authHeaders() });

  if (!res.ok) {
    throw new Error('Error fetching agent nodes');
  }

  const data = (await res.json()) as AgentNodesResponse;
  return Array.isArray(data.nodes) ? data.nodes : [];
}

// ── Create Agent ──
export async function createAgent(
  config: AgentConfig
): Promise<BuilderConfigResponse> {
  const res = await fetch(`${BUILDER_API}/agent/builder/configure`, {
    method: 'POST',
    headers: writeHeaders(),
    body: JSON.stringify({ configurable: config }),
  });

  const data = (await res.json()) as BuilderConfigResponse | ErrorResponse;

  if (!res.ok) {
    const err = data as ErrorResponse;
    throw new Error(err.detail || err.error || 'Error configuring agent');
  }

  return data as BuilderConfigResponse;
}

// ── Create Node ──
export async function createNode(
  agentId: string,
  node: {
    type: string;
    name: string;
    prompt?: string;
    config?: Record<string, unknown>;
  }
): Promise<NodeConfigResponse> {
  const res = await fetch(`${BUILDER_API}/agent/builder/node`, {
    method: 'POST',
    headers: writeHeaders(),
    body: JSON.stringify({
      agent_id: agentId,
      type: node.type,
      name: node.name,
      prompt: node.prompt || null,
      config: node.config || {},
    }),
  });

  const data = (await res.json()) as NodeConfigResponse | ErrorResponse;

  if (!res.ok) {
    const err = data as ErrorResponse;
    throw new Error(err.detail || err.error || 'Error creating node');
  }

  return data as NodeConfigResponse;
}

// ── Update Agent (PATCH) ──
export async function updateAgent(
  agentId: string,
  config: Partial<AgentConfig>
): Promise<BuilderConfigResponse> {
  const res = await fetch(`${BUILDER_API}/agent/builder/agents/${agentId}`, {
    method: 'PATCH',
    headers: writeHeaders(),
    body: JSON.stringify({ configurable: config }),
  });

  const data = (await res.json()) as BuilderConfigResponse | ErrorResponse;

  if (!res.ok) {
    const err = data as ErrorResponse;
    throw new Error(err.detail || err.error || 'Error updating agent');
  }

  return data as BuilderConfigResponse;
}

// ── Activate Agent ──
export async function activateAgent(
  agentId: string
): Promise<ActivationResponse> {
  const res = await fetch(
    `${EXECUTOR_API}/agent/builder/${agentId}/activate`,
    {
      method: 'POST',
      headers: writeHeaders(),
    }
  );

  const data = (await res.json()) as ActivationResponse | ErrorResponse;

  if (!res.ok) {
    const err = data as ErrorResponse;
    throw new Error(err.detail || err.error || 'Error activating agent');
  }

  return data as ActivationResponse;
}

// ── Save Flow Definition ──
export async function saveFlow(
  agentId: string,
  flowDefinition: FlowDefinitionPayload
): Promise<FlowSaveResponse> {
  const res = await fetch(`${BUILDER_API}/agent/builder/flow`, {
    method: 'POST',
    headers: writeHeaders(),
    body: JSON.stringify({
      agent_id: agentId,
      flow_definition: flowDefinition,
    }),
  });

  const data = (await res.json()) as FlowSaveResponse | ErrorResponse;

  if (!res.ok) {
    const err = data as ErrorResponse;
    throw new Error(err.detail || err.error || 'Error saving flow');
  }

  return data as FlowSaveResponse;
}

// ── Fetch Latest Flow Definition ──
export async function fetchFlow(
  agentId: string
): Promise<FlowGetResponse | null> {
  const res = await fetch(
    `${BUILDER_API}/agent/builder/agents/${agentId}/flow`
  );

  if (res.status === 404) {
    return null;
  }

  if (!res.ok) {
    throw new Error('Error fetching flow');
  }

  return (await res.json()) as FlowGetResponse;
}

// ── Save Ontology ──
export async function saveOntology(
  agentId: string,
  ontologies: Array<{
    id: string;
    name: string;
    entities: Array<{
      id: string;
      name: string;
      color: string;
      properties: Array<{ id: string; name: string; type: string }>;
    }>;
    relationships: Array<{
      id: string;
      name: string;
      sourceEntityId: string;
      targetEntityId: string;
    }>;
  }>
): Promise<{ status: string; message: string; agent_id: string; version: number; ontology_id: string }> {
  const res = await fetch(`${BUILDER_API}/agent/builder/ontology`, {
    method: 'POST',
    headers: writeHeaders(),
    body: JSON.stringify({ agent_id: agentId, ontologies }),
  });

  const data = await res.json();

  if (!res.ok) {
    const err = data as ErrorResponse;
    throw new Error(err.detail || err.error || 'Error saving ontology');
  }

  return data;
}

// ── Fetch Latest Ontology ──
export async function fetchOntology(
  agentId: string
): Promise<{ ontology_data: { ontologies: Array<Record<string, unknown>> }; version: number } | null> {
  const res = await fetch(
    `${BUILDER_API}/agent/builder/agents/${agentId}/ontology`
  );

  if (res.status === 404) {
    return null;
  }

  if (!res.ok) {
    throw new Error('Error fetching ontology');
  }

  return await res.json();
}

// ── Delete Agent ──
export async function deleteAgent(agentId: string): Promise<void> {
  const res = await fetch(`${BUILDER_API}/agent/builder/agents/${agentId}`, {
    method: 'DELETE',
    headers: writeHeaders(),
  });

  if (!res.ok) {
    const data = (await res.json()) as ErrorResponse;
    throw new Error(data.detail || data.error || 'Error deleting agent');
  }
}

// ── Marketplace: Register Agent ──
export async function registerMarketplaceAgent(
  key: string
): Promise<BuilderConfigResponse> {
  const res = await fetch(`${BUILDER_API}/agent/builder/marketplace/register`, {
    method: 'POST',
    headers: writeHeaders(),
    body: JSON.stringify({ key }),
  });

  const data = (await res.json()) as BuilderConfigResponse | ErrorResponse;

  if (!res.ok) {
    const err = data as ErrorResponse;
    throw new Error(err.detail || err.error || 'Error registering marketplace agent');
  }

  return data as BuilderConfigResponse;
}

// ── Brain: Fetch file tree ──
export async function fetchBrainTree(): Promise<BrainFile[]> {
  const res = await fetch(`${BUILDER_API}/brain/tree`, { headers: authHeaders() });
  if (!res.ok) throw new Error('Error fetching Brain tree');
  const data = (await res.json()) as { files: BrainFile[] };
  return data.files;
}

// ── Brain: Fetch file content ──
export async function fetchBrainFile(path: string): Promise<BrainFileContent> {
  const res = await fetch(`${BUILDER_API}/brain/file?path=${encodeURIComponent(path)}`, { headers: authHeaders() });
  if (!res.ok) throw new Error('Error fetching Brain file');
  return (await res.json()) as BrainFileContent;
}

// ── Brain: Save (create or update) file ──
/**
 * Brain writes either index inside the same request (the old behavior, kept as
 * the default) or, with `deferIndexing`, only save and leave the
 * indexing to a separate background request (indexBrainFile/deindexBrainPaths).
 */
export interface BrainWriteOptions {
  deferIndexing?: boolean;
}

function deferQuery(options: BrainWriteOptions | undefined, prefix: '?' | '&'): string {
  return options?.deferIndexing ? `${prefix}index=false` : '';
}

export async function saveBrainFile(
  path: string,
  content: string,
  sha?: string,
  options?: BrainWriteOptions,
): Promise<BrainSaveResponse> {
  const res = await fetch(`${BUILDER_API}/brain/file${deferQuery(options, '?')}`, {
    method: 'PUT',
    headers: writeHeaders(),
    body: JSON.stringify({ path, content, sha }),
  });
  const data = (await res.json()) as BrainSaveResponse | ErrorResponse;
  if (!res.ok) {
    const err = data as ErrorResponse;
    throw new Error(err.detail || err.error || 'Error saving Brain file');
  }
  return data as BrainSaveResponse;
}

// ── Brain: Delete file ──
export async function deleteBrainFile(
  path: string,
  sha: string,
  options?: BrainWriteOptions,
): Promise<BrainDeleteResponse> {
  const res = await fetch(
    `${BUILDER_API}/brain/file?path=${encodeURIComponent(path)}&sha=${encodeURIComponent(sha)}${deferQuery(options, '&')}`,
    { method: 'DELETE', headers: writeHeaders() },
  );
  const data = (await res.json()) as BrainDeleteResponse | ErrorResponse;
  if (!res.ok) {
    const err = data as ErrorResponse;
    throw new Error(err.detail || err.error || 'Error deleting Brain file');
  }
  return data as BrainDeleteResponse;
}

// ── Brain: Delete folder (or any path) recursively ──
export async function deleteBrainPath(
  path: string,
  options?: BrainWriteOptions,
): Promise<BrainDeleteFolderResponse> {
  const res = await fetch(
    `${BUILDER_API}/brain/folder?path=${encodeURIComponent(path)}${deferQuery(options, '&')}`,
    { method: 'DELETE', headers: writeHeaders() },
  );
  const data = (await res.json()) as BrainDeleteFolderResponse | ErrorResponse;
  if (!res.ok) {
    const err = data as ErrorResponse;
    throw new Error(err.detail || err.error || 'Error deleting');
  }
  return data as BrainDeleteFolderResponse;
}

async function brainJson<T>(res: Response, fallback: string): Promise<T> {
  const data = (await res.json().catch(() => ({}))) as T | ErrorResponse;
  if (!res.ok) {
    const err = data as ErrorResponse;
    throw new Error(err.detail || err.error || fallback);
  }
  return data as T;
}

/** Indexes the stored version of one document (the background half of a save). The result's `sha` says which version that was. */
export async function indexBrainFile(path: string): Promise<BrainIndexingResult> {
  const res = await fetch(`${BUILDER_API}/brain/index`, {
    method: 'POST',
    headers: writeHeaders(),
    body: JSON.stringify({ path }),
  });
  return brainJson<BrainIndexingResult>(res, 'Error indexing the Brain file');
}

/** Removes already-deleted documents from the index (the background half of a delete). */
export async function deindexBrainPaths(paths: string[]): Promise<BrainIndexingResult> {
  const res = await fetch(`${BUILDER_API}/brain/deindex`, {
    method: 'POST',
    headers: writeHeaders(),
    body: JSON.stringify({ paths }),
  });
  return brainJson<BrainIndexingResult>(res, 'Error removing the Brain file from the index');
}

/** Whether the index holds a document, and since when: how a "pending" request ended. */
export async function fetchBrainPathIndexState(path: string): Promise<BrainPathIndexState> {
  const res = await fetch(`${BUILDER_API}/brain/index-status?path=${encodeURIComponent(path)}`, {
    headers: authHeaders(),
  });
  return brainJson<BrainPathIndexState>(res, 'Error checking the Brain index');
}

// ── Marketplace: Meeting Doubts Analyzer ──
export async function analyzeMeetingDoubts(
  transcript: string,
  title?: string
): Promise<MeetingDoubtsResponse> {
  const payload: MeetingDoubtsRequest = {
    body: {
      transcript: {
        speaker_blocks: [{ speaker: { name: 'Participantes' }, words: transcript }],
      },
      title: title || 'Reunión',
    },
  };

  const res = await fetch(`${EXECUTOR_API}/agent/meetings/v1/doubts`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify([payload]), // API expects List[MeetingEvent]
  });

  if (!res.ok) {
    throw new Error(`Error ${res.status}: ${res.statusText}`);
  }

  return (await res.json()) as MeetingDoubtsResponse;
}

// ── MCP gateway (admin) ──
// Proxied by the executor, which is where admin role checks live.

const MCP_BASE = `${EXECUTOR_API}/agent/builder/mcp`;

async function mcpGet<T>(path: string, errorMessage: string): Promise<T> {
  const res = await fetch(`${MCP_BASE}${path}`, { headers: authHeaders() });
  if (!res.ok) {
    const data = (await res.json().catch(() => ({}))) as ErrorResponse;
    throw new Error(data.detail || data.error || errorMessage);
  }
  return (await res.json()) as T;
}

export async function fetchMcpStatus(serverId: string): Promise<McpServerStatus> {
  return mcpGet<McpServerStatus>(`/servers/${serverId}/status`, 'Error consultando el estado del servidor MCP');
}

/** `?tenant=` for the organization being managed. A tenant admin is pinned to
 * their own by the executor whatever is sent; a platform admin picks. */
function tenantQuery(tenant: string, prefix: '?' | '&'): string {
  return `${prefix}tenant=${encodeURIComponent(tenant)}`;
}

export async function fetchMcpCatalog(serverId: string, tenant: string): Promise<McpCatalog> {
  return mcpGet<McpCatalog>(
    `/servers/${serverId}/catalog${tenantQuery(tenant, '?')}`,
    'Error cargando el catálogo de tools'
  );
}

export async function fetchMcpActivity(
  serverId: string,
  tenant: string,
  limit = 50
): Promise<McpActivityResponse> {
  return mcpGet<McpActivityResponse>(
    `/servers/${serverId}/activity?limit=${limit}${tenantQuery(tenant, '&')}`,
    'Error cargando la actividad reciente'
  );
}

export async function fetchMcpCollections(serverId: string): Promise<string[]> {
  const data = await mcpGet<McpCollectionsResponse>(
    `/servers/${serverId}/collections`,
    'Error listando las colecciones de Mongo'
  );
  return Array.isArray(data.collections) ? data.collections : [];
}

export async function fetchMcpSampleFields(serverId: string, collection: string): Promise<string[]> {
  const data = await mcpGet<McpSampleFieldsResponse>(
    `/servers/${serverId}/sample-fields?collection=${encodeURIComponent(collection)}`,
    'Error muestreando campos de la colección'
  );
  return Array.isArray(data.fields) ? data.fields : [];
}

// ── MCP policy (guardrails) ──
// builder-backend is the policy authority — called directly, not through the
// executor. mcp-gateway polls the same builder-backend endpoint as a client.

const MCP_POLICY_BASE = `${BUILDER_API}/agent/builder/mcp-policy`;

export async function fetchMcpPolicy(serverId: string): Promise<McpPolicyResponse> {
  const res = await fetch(`${MCP_POLICY_BASE}/${serverId}`, { headers: authHeaders() });
  if (!res.ok) {
    const data = (await res.json().catch(() => ({}))) as ErrorResponse;
    throw new Error(data.detail || data.error || 'Error cargando la política de Mongo');
  }
  const data = (await res.json()) as Partial<McpPolicyResponse>;
  return {
    policies: Array.isArray(data.policies) ? data.policies : [],
    globalHiddenFields: data.globalHiddenFields ?? {},
  };
}

export async function saveMcpPolicy(
  serverId: string,
  database: string,
  policies: McpCollectionPolicyInput[],
  globalHiddenFields: string[]
): Promise<void> {
  const res = await fetch(`${MCP_POLICY_BASE}/${serverId}`, {
    method: 'PUT',
    headers: writeHeaders(),
    body: JSON.stringify({ database, policies, globalHiddenFields }),
  });
  if (!res.ok) {
    const data = (await res.json().catch(() => ({}))) as ErrorResponse;
    throw new Error(data.detail || data.error || 'Error guardando la política de Mongo');
  }
}

// ── MCP access (per organization) ──
// builder-backend is the authority; mcp-gateway polls the resolved result.

const MCP_ACCESS_BASE = `${BUILDER_API}/agent/builder/mcp-access`;

async function accessRequest<T>(path: string, errorMessage: string, init?: RequestInit): Promise<T> {
  const res = await fetch(`${MCP_ACCESS_BASE}${path}`, {
    ...init,
    headers: init?.body ? writeHeaders() : authHeaders(),
  });
  if (!res.ok) {
    const data = (await res.json().catch(() => ({}))) as ErrorResponse;
    throw new Error(data.detail || data.error || errorMessage);
  }
  return (await res.json()) as T;
}

export async function fetchMcpTenantAccess(tenant: string): Promise<McpTenantAccess> {
  return accessRequest<McpTenantAccess>(
    `/tenants/${encodeURIComponent(tenant)}`,
    'Error cargando el acceso MCP de la organización'
  );
}

export async function updateMcpTenantServer(
  tenant: string,
  serverId: string,
  patch: { enabled?: boolean; defaultAccess?: McpDefaultAccess }
): Promise<void> {
  await accessRequest<{ server: McpTenantServerAccess }>(
    `/tenants/${encodeURIComponent(tenant)}/servers/${serverId}`,
    'Error actualizando el servidor MCP',
    { method: 'PUT', body: JSON.stringify(patch) }
  );
}

export async function saveMcpAccessRules(
  tenant: string,
  serverId: string,
  rules: McpAccessRule[]
): Promise<McpAccessRule[]> {
  const data = await accessRequest<{ rules: McpAccessRule[] }>(
    `/tenants/${encodeURIComponent(tenant)}/servers/${serverId}/rules`,
    'Error guardando las reglas de acceso',
    { method: 'PUT', body: JSON.stringify({ rules }) }
  );
  return data.rules;
}

export async function saveMcpIdentityProvider(
  tenant: string,
  allowedDomains: string[],
  autoProvision: boolean
): Promise<void> {
  await accessRequest<unknown>(
    `/tenants/${encodeURIComponent(tenant)}/identity-provider`,
    'Error guardando el proveedor de identidad',
    { method: 'PUT', body: JSON.stringify({ type: 'google', allowedDomains, autoProvision }) }
  );
}

export async function fetchBrainIndexStatus(tenant: string): Promise<BrainIndexStatus> {
  return accessRequest<BrainIndexStatus>(
    `/tenants/${encodeURIComponent(tenant)}/brain-status`,
    'Error consultando el estado del índice del Brain'
  );
}

/** One page of a full Brain re-index; call again with `nextOffset` until `done`.
 * Unchanged documents are skipped by hash unless `force` (what a recovery needs:
 * a changed embedding model or a wiped graph leave the hashes intact). */
export async function reindexBrain(offset: number, force = false): Promise<BrainReindexResponse> {
  const res = await fetch(`${BUILDER_API}/brain/reindex?offset=${offset}${force ? '&force=true' : ''}`, {
    method: 'POST',
    headers: authHeaders(),
  });
  if (!res.ok) {
    const data = (await res.json().catch(() => ({}))) as ErrorResponse;
    throw new Error(data.detail || data.error || 'Error reindexando el Brain');
  }
  return (await res.json()) as BrainReindexResponse;
}
